import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [adminExists, setAdminExists] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setAdmin(data);
    } catch {
      setAdmin(false);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/status").then(({ data }) => setAdminExists(data.admin_exists)).catch(() => {});
    checkAuth();
  }, [checkAuth]);

  async function login(email, password) {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAdmin(data);
      setAdminExists(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }

  async function register(email, password, name) {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      if (!adminExists) {
        setAdmin(data);
      }
      setAdminExists(true);
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setAdmin(false);
  }

  return (
    <AuthContext.Provider value={{ admin, adminExists, login, register, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
